import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { programService } from '@/modules/programs/services/programService';
import { resolveDefaultProgramRouteFromApi } from '@/modules/programs/services/resolveDefaultProgramRouteService';
import { appRoutes, programRoutes } from '../constants/routes';
import { SkeletonDashboard } from '../components/skeleton';

export default function DashboardPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch programs accessible by current user
  const { data: programsData, isLoading: programsLoading } = useQuery({
    queryKey: ['myPrograms'],
    queryFn: () => programService.getMyPrograms(),
    enabled: !!user && isAuthenticated && user.role !== 'SuperAdmin' && user.role !== 'ProductAdmin',
  });

  useEffect(() => {
    // Don't redirect if already redirecting or missing auth
    if (isRedirecting || (!user && !isAuthenticated)) {
      return;
    }

    // SuperAdmin should have been redirected already
    if (user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin') {
      setIsRedirecting(true);
      navigate(appRoutes.superadminDashboard(), { replace: true });
      return;
    }

    // If user has a program mapping in their profile, use it
    if (user?.programId) {
      setIsRedirecting(true);
      sessionStorage.setItem('epprem_selected_program', user.programId);
      
      resolveDefaultProgramRouteFromApi(user.programId)
        .then((defaultRoute) => {
          navigate(defaultRoute, { replace: true });
        })
        .catch(() => {
          navigate(programRoutes.workspace(user.programId!), { replace: true });
        });
      return;
    }

    // Wait for programs data to load
    if (programsLoading) {
      return;
    }

    // Handle multiple programs
    if (programsData?.data && programsData.data.length > 0 && user) {
      const activePrograms = programsData.data.filter((program) => program.status === 'ACTIVE');

      if (activePrograms.length === 0) {
        // No active programs - will show error below
        return;
      }

      if (activePrograms.length === 1) {
        // Single program - auto-select
        const selectedProgram = activePrograms[0];
        sessionStorage.setItem('epprem_selected_program', selectedProgram.programId);
        setIsRedirecting(true);

        resolveDefaultProgramRouteFromApi(selectedProgram.programId)
          .then((defaultRoute) => {
            navigate(defaultRoute, { replace: true });
          })
          .catch(() => {
            navigate(programRoutes.workspace(selectedProgram.programId), { replace: true });
          });
        return;
      }

      // Multiple programs - check for previously selected
      const storedProgramId = sessionStorage.getItem('epprem_selected_program');
      const matchedProgram = activePrograms.find((program) => program.programId === storedProgramId);

      if (matchedProgram) {
        setIsRedirecting(true);
        
        resolveDefaultProgramRouteFromApi(matchedProgram.programId)
          .then((defaultRoute) => {
            navigate(defaultRoute, { replace: true });
          })
          .catch(() => {
            navigate(programRoutes.workspace(matchedProgram.programId), { replace: true });
          });
        return;
      }

      // No previously selected program - show selection
      setIsRedirecting(true);
      navigate(appRoutes.programSelection(), { replace: true });
      return;
    }
  }, [user, isAuthenticated, programsData, programsLoading, navigate, isRedirecting]);

  const isLoading = programsLoading;

  // Show loading state
  if (isLoading || isRedirecting || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-[1400px]">
          <SkeletonDashboard kpiCount={5} showGrid />
        </div>
      </div>
    );
  }

  // Show no access message if user has no active program assignments
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-surface-card rounded-lg shadow-elevated p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-warning-soft mb-4">
          <svg className="h-8 w-8 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="app-section-title text-foreground mb-2">No Program Access</h2>
        <p className="app-body-text text-muted-foreground mb-6">
          You do not have access to any active programs. Please contact your administrator to request access.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => navigate(appRoutes.login())}
            className="w-full px-4 py-2 app-body-text bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            Return to Login
          </button>
          <div className="app-body-text text-muted-foreground">
            <p>Logged in as: <span className="font-medium text-foreground">{user.email}</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}
